// useFavorites — small singleton hook for the per-user starred internal-link
// list. Backed by accounts.favorite_links (jsonb) via the user-service. We
// keep it as a module-scoped store so a star toggle in one component reflects
// instantly in the others (sidebar future-use, dashboard, etc.) without
// re-fetching.
//
// Shape stored: [{ to: '/path', label: 'Display', icon: 'LucideIconName' }]

import { useEffect, useSyncExternalStore } from 'react';
import api from '../lib/api';

let state = { loaded: false, list: [] };
const listeners = new Set();
let inflight = null;

function emit() {
  for (const l of listeners) l();
}

function subscribe(l) {
  listeners.add(l);
  return () => listeners.delete(l);
}

function snapshot() {
  return state;
}

async function load() {
  if (state.loaded) return state.list;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const resp = await api.get('/users/me/favorites');
      const list = Array.isArray(resp.data) ? resp.data : [];
      state = { loaded: true, list };
    } catch (_) {
      state = { loaded: true, list: [] };
    } finally {
      inflight = null;
      emit();
    }
    return state.list;
  })();
  return inflight;
}

async function persist(list) {
  // Optimistic: update local state first so the UI doesn't lag the click.
  state = { loaded: true, list };
  emit();
  try {
    const resp = await api.put('/users/me/favorites', { favorites: list });
    if (Array.isArray(resp.data)) {
      state = { loaded: true, list: resp.data };
      emit();
    }
  } catch (_) {
    // Soft fail — we keep the optimistic state; the next reload will reconcile.
  }
}

export function useFavorites() {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (!s.loaded) load();
  }, [s.loaded]);

  const isFavorite = (to) => s.list.some((f) => f.to === to);

  const toggle = (link) => {
    if (!link?.to) return;
    const exists = s.list.some((f) => f.to === link.to);
    const next = exists
      ? s.list.filter((f) => f.to !== link.to)
      : [...s.list, { to: link.to, label: link.label || link.to, icon: link.icon || null }];
    return persist(next);
  };

  const remove = (to) => persist(s.list.filter((f) => f.to !== to));

  return { favorites: s.list, isFavorite, toggle, remove, loaded: s.loaded };
}
